import { Suspense, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { Building2, ChevronLeft, KeyRound, LifeBuoy, Shield, Sparkles, Tags, User, UsersRound } from "lucide-react";
import type { ComponentType } from "react";

import Loader from "src/components/Loader";
import { useAuth } from "src/providers/AuthProvider";
import { PageHead } from "src/components/common";
import { cn } from "src/lib/utils";

interface SettingsLink {
	to: string;
	label: string;
	icon: ComponentType<{ className?: string }>;
}

interface SettingsGroup {
	label: string;
	links: SettingsLink[];
}

/** Mirrors the prototype's settings nav grouping (Account / Business / Team & Access). */
const groups: SettingsGroup[] = [
	{
		label: "Account",
		links: [
			{ to: "/profile", label: "Profile Settings", icon: User },
			{ to: "/account", label: "Account Tier", icon: Sparkles },
		],
	},
	{
		label: "Business",
		links: [
			{ to: "/settings", label: "Brand & Business", icon: KeyRound },
			{ to: "/warehouse", label: "Branches & Warehouses", icon: Building2 },
			{ to: "/tags", label: "Tag Settings", icon: Tags },
		],
	},
	{
		label: "Team & Access",
		links: [
			{ to: "/team", label: "Team Members", icon: UsersRound },
			{ to: "/roles", label: "Roles & Permissions", icon: Shield },
			{ to: "/notifications", label: "Notifications", icon: LifeBuoy },
		],
	},
];

const SettingsLayout = () => {
	const { user } = useAuth();
	const [collapsed, setCollapsed] = useState(false);

	const initials = `${user?.first_name?.[0] ?? ""}${user?.last_name?.[0] ?? ""}`.toUpperCase() || "PL";
	const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "Your account";

	return (
		<div className="tw-space-y-5">
			<PageHead title="Settings" subtitle="Manage your profile, team, access and preferences." />

			<div className={cn("tw-grid tw-gap-4", collapsed ? "lg:tw-grid-cols-[76px,1fr]" : "lg:tw-grid-cols-[260px,1fr]")}>
				<aside className="tw-h-fit tw-rounded-lg tw-border tw-border-border tw-bg-white tw-p-3 tw-shadow-card">
					<div className={cn("tw-mb-3 tw-flex tw-items-center tw-gap-2.5 tw-border-b tw-border-border tw-pb-3", collapsed && "tw-justify-center")}>
						<div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-teal-tint tw-text-[14px] tw-font-bold tw-text-teal-dark">
							{initials}
						</div>
						{!collapsed && (
							<div className="tw-min-w-0 tw-flex-1">
								<div className="tw-truncate tw-text-[13.5px] tw-font-bold tw-text-text">{fullName}</div>
								<div className="tw-truncate tw-text-[12px] tw-text-text-soft">{user?.business_name ?? "Business"}</div>
							</div>
						)}
						<button
							type="button"
							onClick={() => setCollapsed((prev) => !prev)}
							aria-label={collapsed ? "Expand settings menu" : "Collapse settings menu"}
							className="tw-hidden tw-shrink-0 tw-rounded-lg tw-p-1.5 tw-text-text-soft tw-transition-colors hover:tw-bg-bg hover:tw-text-text lg:tw-block"
						>
							<ChevronLeft className={cn("tw-h-4 tw-w-4 tw-transition-transform", collapsed && "tw-rotate-180")} />
						</button>
					</div>

					<nav className="tw-space-y-3">
						{groups.map((group) => (
							<div key={group.label}>
								{!collapsed && (
									<div className="tw-px-2.5 tw-pb-1 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[0.08em] tw-text-text-soft">
										{group.label}
									</div>
								)}
								<div className="tw-space-y-0.5">
									{group.links.map(({ to, label, icon: Icon }) => (
										<NavLink
											key={to}
											to={to}
											title={collapsed ? label : undefined}
											className={({ isActive }) =>
												cn(
													"tw-flex tw-items-center tw-gap-2.5 tw-rounded-[10px] tw-px-2.5 tw-py-2 tw-text-[13.5px] tw-font-medium tw-transition-colors",
													collapsed && "tw-justify-center tw-px-2",
													isActive ? "tw-bg-teal tw-text-white" : "tw-text-text-soft hover:tw-bg-bg hover:tw-text-text"
												)
											}
										>
											<Icon className="tw-h-4 tw-w-4 tw-shrink-0" />
											{!collapsed && <span className="tw-truncate">{label}</span>}
										</NavLink>
									))}
								</div>
							</div>
						))}
					</nav>
				</aside>

				<div className="tw-min-w-0">
					<Suspense fallback={<Loader />}>
						<Outlet />
					</Suspense>
				</div>
			</div>
		</div>
	);
};

export default SettingsLayout;
